import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit3,
  ListPlus,
  Loader2,
  Plus,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { productsApi } from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Supplements/supplements.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SUPPLEMENTS_PER_PAGE = 10;

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return value === key ? fallback : value;
};


const getBooleanStatus = (value) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value === 'true';

const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};

const formatPrice = (value, language) => {
  const number = Number(value || 0);

  try {
    return `${new Intl.NumberFormat(language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number)} MAD`;
  } catch {
    return `${number.toFixed(2)} MAD`;
  }
};


function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = '—',
  ariaLabel,
  className = '',
  portal = false,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  // Position table dropdowns outside the table so they are never clipped.
  const updatePortalPosition = () => {
    if (!portal || !rootRef.current) {
      return;
    }

    const rect =
      rootRef.current.getBoundingClientRect();

    const direction =
      window.getComputedStyle(rootRef.current)
        .direction ||
      document.documentElement.dir ||
      'ltr';

    const viewportPadding = 8;
    const gap = 8;
    const estimatedMenuHeight = Math.min(
      options.length * 38 +
        Math.max(options.length - 1, 0) * 4 +
        12,
      240,
    );

    const spaceBelow =
      window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openAbove =
      spaceBelow < estimatedMenuHeight + gap &&
      spaceAbove > spaceBelow;

    let top = openAbove
      ? rect.top - estimatedMenuHeight - gap
      : rect.bottom + gap;

    top = Math.max(
      viewportPadding,
      Math.min(
        top,
        window.innerHeight -
          estimatedMenuHeight -
          viewportPadding,
      ),
    );

    const width = Math.max(rect.width, 145);

    let left =
      direction === 'rtl'
        ? rect.right - width
        : rect.left;

    left = Math.max(
      viewportPadding,
      Math.min(
        left,
        window.innerWidth -
          width -
          viewportPadding,
      ),
    );

    setPortalStyle({
      top,
      left,
      width,
      direction,
      placement: openAbove
        ? 'above'
        : 'below',
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger =
        rootRef.current?.contains(event.target);

      const clickedPortal =
        menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPortal) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return undefined;
    }

    updatePortalPosition();

    const frame = window.requestAnimationFrame(
      updatePortalPosition,
    );

    window.addEventListener(
      'resize',
      updatePortalPosition,
    );
    window.addEventListener(
      'scroll',
      updatePortalPosition,
      true,
    );

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener(
        'resize',
        updatePortalPosition,
      );
      window.removeEventListener(
        'scroll',
        updatePortalPosition,
        true,
      );
    };
    // Reposition when the open state or menu size changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal, options.length]);

  const renderOptions = () =>
    options.map((option) => {
      const active =
        String(option.value) ===
        String(value);

      return (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={active}
          className={
            active
              ? 'is-active'
              : ''
          }
          onClick={() => {
            onChange?.(
              option.value,
            );

            setOpen(false);
          }}
        >
          <span>{option.label}</span>

          {active ? (
            <Check size={15} />
          ) : null}
        </button>
      );
    });

  const portalMenu =
    open &&
    portal &&
    portalStyle
      ? createPortal(
          <div
            ref={menuRef}
            className={`supplements-custom-select__menu supplements-custom-select__menu--portal ${
              portalStyle.placement === 'above'
                ? 'is-above'
                : 'is-below'
            }`}
            role="listbox"
            dir={portalStyle.direction}
            style={{
              top: `${portalStyle.top}px`,
              left: `${portalStyle.left}px`,
              width: `${portalStyle.width}px`,
            }}
          >
            {renderOptions()}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`supplements-custom-select ${className}`}
      >
        <button
          type="button"
          className={`supplements-custom-select__trigger ${
            open ? 'is-open' : ''
          }`}
          onClick={() => {
            if (!disabled) {
              if (!open && portal) {
                updatePortalPosition();
              }

              setOpen(
                (current) =>
                  !current,
              );
            }
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>
            {selectedOption?.label ||
              placeholder}
          </span>

          <ChevronDown
            size={16}
            className={
              open
                ? 'is-rotated'
                : ''
            }
          />
        </button>

        {open && !portal ? (
          <div
            className="supplements-custom-select__menu"
            role="listbox"
          >
            {renderOptions()}
          </div>
        ) : null}
      </div>

      {portalMenu}
    </>
  );
}

function Supplements() {
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const [supplements, setSupplements] = useState([]);
  const [meta, setMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [error, setError] = useState('');
  const [updatingSupplementId, setUpdatingSupplementId] = useState(null);

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const summary = useMemo(() => {
    return {
      total: Number(meta?.total || supplements.length),
      active: supplements.filter(
        (supplement) => supplement.is_active,
      ).length,
      inactive: supplements.filter(
        (supplement) => !supplement.is_active,
      ).length,
      filtered: Number(meta?.total || supplements.length),
    };
  }, [supplements, meta]);


  const fetchSupplements = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError('');

      const response = await productsApi.getSupplements(
        establishmentId,
        {
          search: searchTerm || undefined,
          is_active:
            selectedStatus === ''
              ? undefined
              : selectedStatus === 'active'
                ? 1
                : 0,
          locale: language,
          page: currentPage,
          per_page: SUPPLEMENTS_PER_PAGE,
        },
      );

      setSupplements(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSupplements();
    }, 350);

    return () => clearTimeout(timeout);
  }, [
    establishmentId,
    searchTerm,
    selectedStatus,
    currentPage,
    language,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus]);

  useEffect(() => {
    const lastPage = Number(meta?.last_page || 1);

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [meta?.last_page, currentPage]);

  const handleDelete = async (supplement) => {
    const supplementName =
      getTranslatedField(supplement, 'name', language, 'fr') ||
      t('supplements.noName');

    const confirmed = await confirm({
      title: t('supplements.actions.delete'),
      message: t('supplements.confirmDelete', { name: supplementName }),
      itemName: supplementName,
      confirmText: t('supplements.actions.delete'),
      cancelText: t('common.cancel'),
      closeText: t('common.close'),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await productsApi.deleteSupplement(supplement.id);

      await fetchSupplements();

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('supplements.alerts.deleted'),
      });
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    }
  };


  const handleStatusChange = async (
    supplement,
    nextStatus,
  ) => {
    const isActive =
      nextStatus === 'active';

    if (
      Boolean(supplement.is_active) ===
      isActive
    ) {
      return;
    }

    try {
      setUpdatingSupplementId(
        supplement.id,
      );

      await productsApi.updateSupplement(
        supplement.id,
        {
          price:
            supplement.price || 0,
          sort_order:
            supplement.sort_order || 0,
          is_active:
            isActive,
          translations:
            supplement.translations || {},
        },
      );

      setSupplements(
        (currentSupplements) =>
          currentSupplements.map(
            (item) =>
              item.id ===
              supplement.id
                ? {
                    ...item,
                    is_active:
                      isActive,
                  }
                : item,
          ),
      );

      if (selectedStatus) {
        await fetchSupplements({
          silent: true,
        });
      }

      showAlert({
        type: 'success',
        title: t(
          'alerts.success',
        ),
        message: translateFallback(
          t,
          'supplements.alerts.statusUpdated',
          isActive
            ? t(
                'supplements.status.active',
              )
            : t(
                'supplements.status.inactive',
              ),
        ),
      });
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(
        translatedError,
      );

      showAlert({
        type: 'error',
        title: t(
          'alerts.error',
        ),
        message:
          translatedError,
      });
    } finally {
      setUpdatingSupplementId(
        null,
      );
    }
  };

  const totalPages = Math.max(
    1,
    Number(meta?.last_page || 1),
  );

  const activePage = Math.min(
    Math.max(1, Number(meta?.current_page || currentPage)),
    totalPages,
  );

  const paginationStart = Number(meta?.from || 0);
  const paginationEnd = Number(meta?.to || 0);
  const paginationTotal = Number(meta?.total || supplements.length);

  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;

    const firstPage = Math.max(
      1,
      Math.min(
        activePage - 2,
        totalPages - maxVisiblePages + 1,
      ),
    );

    const lastPage = Math.min(
      totalPages,
      firstPage + maxVisiblePages - 1,
    );

    return Array.from(
      {
        length: Math.max(
          0,
          lastPage - firstPage + 1,
        ),
      },
      (_, index) => firstPage + index,
    );
  }, [activePage, totalPages]);

  if (loading) {
    return (
      <div className="supplements-page" dir={direction}>
        <div className="supplements-empty-state">
          <Loader2 size={34} />
          <h2>{t('supplements.loading')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="supplements-page" dir={direction}>
      <section className="supplements-heading">
        <div>
          <p className="supplements-heading__eyebrow">
            {t('supplements.eyebrow')}
          </p>
          <h1>{t('supplements.title')}</h1>
          <p>{t('supplements.description')}</p>
        </div>

        <Link to="/supplements/new" className="dashboard-primary-button">
          <Plus size={18} />
          {t('supplements.addSupplement')}
        </Link>
      </section>

      <section className="supplements-summary-grid">
        <article className="supplements-summary-card">
          <span>
            <ListPlus size={22} />
          </span>
          <div>
            <strong>{formatNumber(summary.total, language)}</strong>
            <p>{t('supplements.summary.total')}</p>
          </div>
        </article>

        <article className="supplements-summary-card">
          <span>
            <CheckCircle2 size={22} />
          </span>
          <div>
            <strong>{formatNumber(summary.active, language)}</strong>
            <p>{t('supplements.summary.active')}</p>
          </div>
        </article>

        <article className="supplements-summary-card">
          <span>
            <XCircle size={22} />
          </span>
          <div>
            <strong>{formatNumber(summary.inactive, language)}</strong>
            <p>{t('supplements.summary.inactive')}</p>
          </div>
        </article>

        <article className="supplements-summary-card">
          <span>
            <Search size={22} />
          </span>
          <div>
            <strong>{formatNumber(summary.filtered, language)}</strong>
            <p>{t('supplements.summary.filtered')}</p>
          </div>
        </article>
      </section>

      <section className="supplements-panel">
        <div className="supplements-toolbar">
          <label className="supplements-search">
            <Search size={18} />
            <input
              type="search"
              value={searchTerm}
              placeholder={t('supplements.searchPlaceholder')}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>

          <CustomSelect
            className="supplements-filter"
            value={selectedStatus}
            onChange={setSelectedStatus}
            ariaLabel={t(
              'supplements.filters.allStatuses',
            )}
            options={[
              {
                value: '',
                label: t(
                  'supplements.filters.allStatuses',
                ),
              },
              {
                value: 'active',
                label: t(
                  'supplements.status.active',
                ),
              },
              {
                value: 'inactive',
                label: t(
                  'supplements.status.inactive',
                ),
              },
            ]}
          />
        </div>

        {error && (
          <div className="supplements-alert supplements-alert--error">
            {error}
          </div>
        )}

        {supplements.length === 0 ? (
          <div className="supplements-empty-state">
            <ListPlus size={38} />
            <h2>{t('supplements.empty.title')}</h2>
            <p>{t('supplements.empty.description')}</p>
          </div>
        ) : (
          <div className="supplements-table-wrapper">
            <table className="supplements-table">
              <thead>
                <tr>
                  <th>{t('supplements.table.supplement')}</th>
                  <th>{t('supplements.table.price')}</th>
                  <th>{t('supplements.table.sortOrder')}</th>
                  <th>{t('supplements.table.status')}</th>
                  <th>{t('supplements.table.actions')}</th>
                </tr>
              </thead>

              <tbody>
                {supplements.map((supplement) => {
                  const name =
                    getTranslatedField(supplement, 'name', language, 'fr') ||
                    t('supplements.noName');

                  const description =
                    getTranslatedField(
                      supplement,
                      'description',
                      language,
                      'fr',
                    ) || t('supplements.noDescription');

                  return (
                    <tr key={supplement.id}>
                      <td>
                        <div className="supplements-table__supplement">
                          <span className="supplements-table__icon">
                            <ListPlus size={22} />
                          </span>

                          <div>
                            <strong>{name}</strong>
                            <small>{description}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="supplements-table__price">
                          <strong>{formatPrice(supplement.price, language)}</strong>
                        </div>
                      </td>

                      <td>{formatNumber(supplement.sort_order || 0, language)}</td>

                      <td>
                        <CustomSelect
                          value={
                            supplement.is_active
                              ? 'active'
                              : 'inactive'
                          }
                          onChange={(value) =>
                            handleStatusChange(
                              supplement,
                              value,
                            )
                          }
                          disabled={
                            updatingSupplementId ===
                            supplement.id
                          }
                          ariaLabel={t(
                            'supplements.table.status',
                          )}
                          className="supplement-status-select"
                          portal
                          options={[
                            {
                              value: 'active',
                              label: t(
                                'supplements.status.active',
                              ),
                            },
                            {
                              value: 'inactive',
                              label: t(
                                'supplements.status.inactive',
                              ),
                            },
                          ]}
                        />
                      </td>

                      <td>
                        <div className="supplements-table__actions">
                          <Link
                            to={`/supplements/${supplement.id}/edit`}
                            title={t('supplements.actions.edit')}
                          >
                            <Edit3 size={16} />
                          </Link>

                          <button
                            type="button"
                            className="supplements-table__delete"
                            title={t('supplements.actions.delete')}
                            onClick={() => handleDelete(supplement)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && paginationTotal > 0 ? (
          <div className="supplements-pagination">
            <div className="supplements-pagination__summary">
              <span>
                {translateFallback(
                  t,
                  'common.showing',
                  'Showing',
                )}
              </span>

              <strong>
                {formatNumber(paginationStart, language)} - {formatNumber(paginationEnd, language)}
              </strong>

              <span>
                {translateFallback(
                  t,
                  'common.of',
                  'of',
                )}
              </span>

              <strong>{formatNumber(paginationTotal, language)}</strong>
            </div>

            <div className="supplements-pagination__controls">
              <button
                type="button"
                className="supplements-pagination__button"
                disabled={activePage <= 1}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.max(1, page - 1),
                  )
                }
              >
                <ChevronLeft size={17} />

                {translateFallback(
                  t,
                  'common.previous',
                  'Previous',
                )}
              </button>

              <div className="supplements-pagination__pages">
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={formatNumber(pageNumber, language)}
                    type="button"
                    className={
                      activePage === pageNumber
                        ? 'active'
                        : ''
                    }
                    onClick={() =>
                      setCurrentPage(pageNumber)
                    }
                  >
                    {formatNumber(pageNumber, language)}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="supplements-pagination__button"
                disabled={activePage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(
                      totalPages,
                      page + 1,
                    ),
                  )
                }
              >
                {translateFallback(
                  t,
                  'common.next',
                  'Next',
                )}

                <ChevronRight size={17} />
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default Supplements;